import { useContext, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ChannelType, TemplateProofParams } from '../../../types'
import Modal, { ModalProps } from '../../../ui/Modal'
import FormWrapper from '../../../ui/form/FormWrapper'
import TextInput from '../../../ui/form/TextInput'
import { Button, DataTable, Heading } from '../../../ui'
import { CloseIcon } from '../../../ui/icons'
import api from '../../../api'
import { ProjectContext } from '../../../contexts'
import { toast } from 'react-hot-toast/headless'

const PROOF_RECIPIENTS_KEY = 'parcelvoy:proof_recipients'
const MAX_RECENT_RECIPIENTS = 5

const getRecentRecipients = (): string[] => {
    try {
        const stored = sessionStorage.getItem(PROOF_RECIPIENTS_KEY)
        return stored ? JSON.parse(stored) : []
    } catch {
        return []
    }
}

const addRecentRecipient = (recipient: string): void => {
    const recipients = getRecentRecipients().filter(r => r !== recipient)
    recipients.unshift(recipient)
    sessionStorage.setItem(PROOF_RECIPIENTS_KEY, JSON.stringify(recipients.slice(0, MAX_RECENT_RECIPIENTS)))
}

interface SendProofProps extends Omit<ModalProps, 'title'> {
    templateId: number
    type: ChannelType
    variables?: Record<string, any>
    onBeforeSend?: () => Promise<void>
    onResponse?: (response: any) => void
}

export default function CampaignSendProof({ open, onClose, templateId, type, variables = {}, onBeforeSend, onResponse }: SendProofProps) {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [recentRecipients, setRecentRecipients] = useState<string[]>([])

    useEffect(() => {
        if (open) {
            setRecentRecipients(getRecentRecipients())
        }
    }, [open])

    const handleSubmit = async (recipient: string) => {
        addRecentRecipient(recipient)
        if (onBeforeSend) {
            await onBeforeSend()
        }
        try {
            const response = await api.templates.proof(project.id, templateId, {
                variables,
                recipient,
            })
            onResponse?.(response)
        } catch (error: any) {
            if (error?.response?.data) {
                if (typeof error?.response?.data.error === 'string') {
                    toast.error(error.response.data.error)
                    return
                }
                onResponse?.(error.response)
            }
            toast.error(error.message)
            return
        }
        onClose(false)
        type === 'webhook'
            ? toast.success(t('webhook_test_sent'))
            : toast.success(t('template_proof_sent'))
    }

    const handleRemoveRecipient = (recipient: string) => {
        const updated = recentRecipients.filter(r => r !== recipient)
        setRecentRecipients(updated)
        sessionStorage.setItem(PROOF_RECIPIENTS_KEY, JSON.stringify(updated))
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={t('send_proof')}
            description={t(type === 'email' ? 'send_proof_email_desc' : 'send_proof_other_desc')}>
            <FormWrapper<TemplateProofParams>
                key={open ? 'open' : 'closed'}
                submitLabel={t('send')}
                onSubmit={async ({ recipient }) => await handleSubmit(recipient)}>
                {form => (
                    <TextInput.Field
                        form={form}
                        name="recipient"
                        required
                        clearable
                    />
                )}
            </FormWrapper>
            {recentRecipients.length > 0 && <>
                <Heading size="h4" title={t('recent')} />
                <DataTable
                    items={recentRecipients.map(recipient => ({ recipient }))}
                    itemKey={({ item }) => item.recipient}
                    onSelectRow={async (row) => await handleSubmit(row.recipient)}
                    columns={[
                        {
                            key: 'recipient',
                            title: t('user'),
                        },
                        {
                            key: 'options',
                            title: '',
                            align: 'end',
                            cell: ({ item }) => (
                                <Button
                                    size="small"
                                    variant="destructive_secondary"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleRemoveRecipient(item.recipient)
                                    }}
                                    icon={<CloseIcon />}
                                >
                                    {t('remove')}
                                </Button>
                            ),
                        },
                    ]}
                />
            </>}
        </Modal>
    )
}
